/**
 * Add/edit form for a coupon package: name, status, and one level per reward tier.
 * Inputs stay strings while editing; validation mirrors the backend.
 */

export const LIMITS = { name: 100, brandName: 100, offerText: 255, offerSubtitle: 255, couponCode: 50, logoUrl: 1000 };
export const VALID_DAYS = { min: 1, max: 3650 };
export const LEVEL_FIELDS = ['brandName', 'logoUrl', 'offerText', 'offerSubtitle', 'couponCode', 'validForDays'];
const CODE_RE = /^[A-Za-z0-9_-]+$/;

const emptyLevel = () => ({ brandName: '', logoUrl: '', offerText: '', offerSubtitle: '', couponCode: '', validForDays: '' });

/** Blank form with one level per tier. */
export function emptyForm(levelCount) {
  return { name: '', status: 'draft', levels: Array.from({ length: levelCount }, emptyLevel) };
}

/**
 * Form from an existing package, padded to `levelCount` levels.
 * @param {import('./couponSchema').Package} pkg
 * @param {{ copy?: boolean }} [options]  copy = "Duplicate": "Copy of …" name, status draft
 */
export function toForm(pkg, levelCount, { copy = false } = {}) {
  const levels = Array.from({ length: levelCount }, (_, i) => {
    const l = pkg.levels.find((x) => x.level === i + 1);
    if (!l) return emptyLevel();
    return {
      brandName: l.brandName ?? '',
      logoUrl: l.logoUrl ?? '',
      offerText: l.offerText ?? '',
      offerSubtitle: l.offerSubtitle ?? '',
      couponCode: l.couponCode ?? '',
      validForDays: l.validForDays == null ? '' : String(l.validForDays),
    };
  });
  const baseName = pkg.name?.trim() || `Package #${pkg.id}`;
  return {
    name: copy ? `Copy of ${baseName}`.slice(0, LIMITS.name) : pkg.name ?? '',
    status: copy ? 'draft' : pkg.status,
    levels,
  };
}

export function isDirty(form, initial) {
  if (form.name !== initial.name || form.status !== initial.status) return true;
  return form.levels.some((level, i) => LEVEL_FIELDS.some((f) => level[f] !== initial.levels[i]?.[f]));
}

function isHttpUrl(value) {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Returns `{ name?, status?, levels: { [index]: { [field]: message } } }`; empty when valid.
 * `hasErrors(result)` tells whether anything failed.
 */
export function validateForm(form) {
  const result = { levels: {} };
  const add = (i, field, message) => {
    result.levels[i] = { ...result.levels[i], [field]: message };
  };
  if (form.name.trim().length > LIMITS.name) result.name = `Use at most ${LIMITS.name} characters.`;
  if (!['draft', 'active', 'archived'].includes(form.status)) result.status = 'Choose a status.';

  form.levels.forEach((level, i) => {
    const brand = level.brandName.trim();
    const offer = level.offerText.trim();
    const subtitle = level.offerSubtitle.trim();
    const code = level.couponCode.trim();
    const logo = level.logoUrl.trim();
    const days = String(level.validForDays).trim();

    if (!brand) add(i, 'brandName', 'Brand name is required.');
    else if (brand.length > LIMITS.brandName) add(i, 'brandName', `Use at most ${LIMITS.brandName} characters.`);

    if (!offer) add(i, 'offerText', 'Offer text is required.');
    else if (offer.length > LIMITS.offerText) add(i, 'offerText', `Use at most ${LIMITS.offerText} characters.`);

    if (subtitle.length > LIMITS.offerSubtitle) add(i, 'offerSubtitle', `Use at most ${LIMITS.offerSubtitle} characters.`);

    if (code && (code.length > LIMITS.couponCode || !CODE_RE.test(code))) {
      add(i, 'couponCode', `Use up to ${LIMITS.couponCode} letters, digits, - or _.`);
    }

    if (logo && (logo.length > LIMITS.logoUrl || !isHttpUrl(logo))) {
      add(i, 'logoUrl', 'Enter a full http(s) URL, e.g. https://cdn.example.com/logo.png.');
    }

    if (days) {
      const n = Number(days);
      if (!/^\d+$/.test(days) || n < VALID_DAYS.min || n > VALID_DAYS.max) {
        add(i, 'validForDays', `Enter a whole number of days from ${VALID_DAYS.min} to ${VALID_DAYS.max}, or choose No expiry.`);
      }
    }
  });
  return result;
}

export const hasErrors = (errors) => Boolean(errors.name || errors.status || Object.keys(errors.levels ?? {}).length);

const orNull = (value) => (value.trim() ? value.trim() : null);

/** POST/PUT body: trimmed, empty optionals as null, coupon codes upper-case, offerText (not couponDetails). */
export function toPayload(form) {
  return {
    name: orNull(form.name),
    status: form.status,
    levels: form.levels.map((l) => ({
      brandName: l.brandName.trim(),
      logoUrl: orNull(l.logoUrl),
      offerText: l.offerText.trim(),
      offerSubtitle: orNull(l.offerSubtitle),
      couponCode: l.couponCode.trim() ? l.couponCode.trim().toUpperCase() : null,
      validForDays: String(l.validForDays).trim() ? Number(l.validForDays) : null,
    })),
  };
}

/**
 * Maps the backend's `[{ field, message }]` onto the form:
 * "name" / "status" → top fields, "levels[i].x" → card i field x, anything else → `general`.
 */
export function mapServerErrors(fieldErrors = []) {
  const errors = { levels: {} };
  const general = [];
  for (const { field, message } of fieldErrors) {
    const match = /^levels\[(\d+)\]\.(\w+)$/.exec(field ?? '');
    if (match && LEVEL_FIELDS.includes(match[2])) {
      const i = Number(match[1]);
      errors.levels[i] = { ...errors.levels[i], [match[2]]: message };
    } else if (field === 'name' || field === 'status') {
      errors[field] = message;
    } else {
      general.push(message);
    }
  }
  return { errors, general };
}

/** DOM id of an input, used to focus/scroll to the first invalid one. */
export const fieldId = (index, field) => `coupon-l${index + 1}-${field}`;

export function firstErrorId(errors) {
  if (errors.name) return 'coupon-name';
  if (errors.status) return 'coupon-status';
  const indexes = Object.keys(errors.levels ?? {})
    .map(Number)
    .sort((a, b) => a - b);
  for (const i of indexes) {
    const field = LEVEL_FIELDS.find((f) => errors.levels[i]?.[f]);
    if (field) return fieldId(i, field);
  }
  return null;
}
